import { RestrictedHeadings } from '../components/typography/typography.component';

export const debounce = (func: (...args: unknown[]) => void, wait: number) => {
  let timeout: ReturnType<typeof setTimeout>;

  const debouncedFunction = (...args: unknown[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };

  debouncedFunction.cancel = function () {
    clearTimeout(timeout);
  };

  return debouncedFunction;
};

export const isEqual = (value: any, other: any) => {
  if (typeof value !== 'object' && typeof other !== 'object') {
    return Object.is(value, other);
  }

  if (value === null && other === null) {
    return true;
  }

  if (typeof value !== typeof other) {
    return false;
  }

  if (value === other) {
    return true;
  }

  if (Array.isArray(value) && Array.isArray(other)) {
    if (value.length !== other.length) {
      return false;
    }

    for (let i = 0; i < value.length; i++) {
      if (!isEqual(value[i], other[i])) {
        return false;
      }
    }

    return true;
  }

  if (Array.isArray(value) || Array.isArray(other)) {
    return false;
  }

  if (Object.keys(value).length !== Object.keys(other).length) {
    return false;
  }

  for (const [k, v] of Object.entries(value)) {
    if (!(k in other)) {
      return false;
    }

    if (!isEqual(v, other[k])) {
      return false;
    }
  }

  return true;
};

export const isObjectEmpty = (value: unknown): boolean => {
  let parsedValue = value;

  if (typeof value === 'string') {
    try {
      parsedValue = JSON.parse(value);
    } catch (_error) {
      return false;
    }
  }

  const isObject =
    typeof parsedValue === 'object' &&
    parsedValue !== null &&
    parsedValue !== undefined;
  const isEmptyObject =
    isObject && Object.keys(parsedValue as object).length === 0;

  return isEmptyObject;
};

export const incrementHeadingLevel = (
  containerHeadingLevel: RestrictedHeadings
): RestrictedHeadings | null => {
  const headingLevels: RestrictedHeadings[] = ['h2', 'h3', 'h4', 'h5', 'h6'];
  const currentLevelIndex = headingLevels.indexOf(containerHeadingLevel);

  if (currentLevelIndex === -1) {
    return null; // Invalid heading level
  }

  const nextLevelIndex = currentLevelIndex + 1;

  if (nextLevelIndex >= headingLevels.length) {
    return null; // Cannot increment beyond h6
  }

  return headingLevels[nextLevelIndex];
};

export const parseHeading = (
  heading: RestrictedHeadings,
  fallback: RestrictedHeadings,
  fullRange = false
): RestrictedHeadings => {
  const regex = fullRange ? /h[1-6]/ : /h[2-5]/;
  return (heading?.match(regex)?.[0] as RestrictedHeadings) ?? fallback;
};
